import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Clock, Users, Wine, Utensils, Camera, MapPin } from 'lucide-react';

const Tours = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const tours = [
    {
      title: "Tour Clásico",
      duration: "1.5 horas",
      people: "2-20 personas",
      price: "Consultar",
      icon: <Wine className="h-8 w-8" />,
      features: [
        "Visita guiada por el establecimiento",
        "Recorrido por viñedos centenarios",
        "Conoce nuestra cava de piedra",
        "Degustación de 3 vinos"
      ],
      image: "https://images.unsplash.com/photo-1516594915697-87eb3b1c14ea?crop=entropy&cs=srgb&fm=jpg&q=85"
    },
    {
      title: "Experiencia Gourmet",
      duration: "3-4 horas",
      people: "2-15 personas",
      price: "Premium",
      icon: <Utensils className="h-8 w-8" />,
      features: [
        "Tour completo por la bodega",
        "Almuerzo maridaje 4 pasos",
        "Degustación de vinos premium",
        "Atención de sommelier profesional"
      ],
      image: "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?crop=entropy&cs=srgb&fm=jpg&q=85"
    },
    {
      title: "Experiencia Cosecha",
      duration: "2-3 horas",
      people: "4-12 personas",
      price: "Temporada",
      icon: <Camera className="h-8 w-8" />,
      features: [
        "Participá en la cosecha de uvas",
        "Pisá uvas en barrica tradicional",
        "Tour fotográfico por viñedos",
        "Degustación y brindis especial"
      ],
      image: "https://images.unsplash.com/photo-1562601579-599dec564e06?crop=entropy&cs=srgb&fm=jpg&q=85"
    }
  ];

  const scrollToReservation = () => {
    const element = document.getElementById('reservation');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="tours" className="py-20 bg-gray-900 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Tours y Experiencias
          </h2>
          <div className="w-24 h-1 mx-auto mb-6" style={{background: 'linear-gradient(90deg, #D4AF37 0%, #C9A961 100%)'}}></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Descubrí nuestra bodega con experiencias únicas diseñadas para todos los gustos
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {tours.map((tour, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.2, duration: 0.6 }}
              whileHover={{ y: -10 }}
              className="group bg-gray-800 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all"
              data-testid={`tour-card-${index}`}
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={tour.image}
                  alt={tour.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent"></div>
                <div className="absolute top-4 right-4 text-white p-3 rounded-full shadow-lg" style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}>
                  {tour.icon}
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-2xl font-bold mb-4">{tour.title}</h3>
                
                <div className="flex items-center gap-4 mb-4 text-gray-400">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    <span className="text-sm">{tour.duration}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    <span className="text-sm">{tour.people}</span>
                  </div>
                </div>

                <ul className="space-y-2 mb-6">
                  {tour.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-300">
                      <span className="mt-1" style={{color: '#D4AF37'}}>✓</span>
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="flex items-center justify-between pt-4 border-t border-gray-700">
                  <span className="text-xl font-bold" style={{color: '#D4AF37'}}>{tour.price}</span>
                  <motion.button
                    onClick={scrollToReservation}
                    className="text-white px-6 py-2 rounded-full font-bold transition-colors"
                    style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    data-testid={`tour-reservar-btn-${index}`}
                  >
                    Reservar
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Info Banner */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="mt-12 bg-amber-900/20 rounded-2xl p-8 text-center backdrop-blur-sm"
          style={{border: '1px solid rgba(212, 175, 55, 0.3)'}}
        >
          <MapPin className="h-8 w-8 mx-auto mb-4" style={{color: '#D4AF37'}} />
          <h4 className="text-2xl font-bold mb-2">¡Visitas todos los días!</h4>
          <p className="text-gray-300">
            Abierto de Martes a Domingo. Reservas con anticipación.
            <br />
            A solo minutos del centro de Montevideo.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Tours;
